// lib/slides/noteLayout.ts
import type { SlideElementFrame } from '#types/types';
import { clampFrameToPage } from './annotationLayout';
import { measureNoteText, noteDisplayText, NOTE_CORNER_RADIUS, NOTE_FONT_PT, NOTE_LINE_STEP, NOTE_PAD_PT, pageScaleFromA4 } from './noteMetrics';
import { layoutLineLimit, wrapStampText } from './stampLayout';

export const NOTE_OVERFLOW_MESSAGE = 'This comment does not fit. Shorten it, widen it or enlarge the page.';

export function buildNoteLayout(input: {
  frame: SlideElementFrame;
  text: string;
  rotation?: number;
  anchor?: { x: number; y: number };
  opacity?: number;
}, pageWidth: number, pageHeight: number) {
  const scale = pageScaleFromA4(pageWidth, pageHeight);
  const fontSize = NOTE_FONT_PT * scale;
  const padding = NOTE_PAD_PT * scale;
  const lineHeight = fontSize * NOTE_LINE_STEP;
  const strokeWidth = 0.8 * scale;
  const stored = input.frame;
  const normalized = {
    x: Math.min(stored.x, stored.x + stored.w),
    y: Math.min(stored.y, stored.y + stored.h),
    w: Math.abs(stored.w), h: Math.abs(stored.h),
  };
  const clamped = clampFrameToPage(normalized, pageWidth, pageHeight);
  const width = Math.max(1, Math.min(normalized.w, clamped.w, pageWidth - 16 - strokeWidth));
  const usableWidth = width - 2 * padding;
  const text = noteDisplayText(input.text ?? '');
  const lineLimit = layoutLineLimit(pageHeight, lineHeight);
  const lines = wrapStampText(text, fontSize, false, usableWidth, measureNoteText, lineLimit);
  const lineWidths = lines.map(line => measureNoteText(line, fontSize));
  const neededHeight = padding * 2 + lines.length * lineHeight + 1.5 * scale;
  const height = Math.max(Math.min(normalized.h, clamped.h, pageHeight - 16 - strokeWidth), neededHeight);
  const rotation = Number.isFinite(input.rotation) ? input.rotation! : 0;
  const angle = (rotation % 360) * Math.PI / 180;
  const cos = Math.cos(angle), sin = Math.sin(angle);
  const halfPaintWidth = (Math.abs(cos) * width + Math.abs(sin) * height + strokeWidth) / 2;
  const halfPaintHeight = (Math.abs(sin) * width + Math.abs(cos) * height + strokeWidth) / 2;
  const fitsX = halfPaintWidth * 2 <= pageWidth - 16 + 1e-7;
  const fitsY = halfPaintHeight * 2 <= pageHeight - 16 + 1e-7;
  const centre = {
    x: fitsX ? Math.min(Math.max(normalized.x + width / 2, 8 + halfPaintWidth), pageWidth - 8 - halfPaintWidth) : pageWidth / 2,
    y: fitsY ? Math.min(Math.max(normalized.y + height / 2, 8 + halfPaintHeight), pageHeight - 8 - halfPaintHeight) : pageHeight / 2,
  };
  const frame = { x: centre.x - width / 2, y: centre.y - height / 2, w: width, h: height };
  const radius = Math.max(0, Math.min(NOTE_CORNER_RADIUS * scale, width / 2, height / 2));
  const path = `M ${radius} 0 H ${width - radius} A ${radius} ${radius} 0 0 1 ${width} ${radius} V ${height - radius} A ${radius} ${radius} 0 0 1 ${width - radius} ${height} H ${radius} A ${radius} ${radius} 0 0 1 0 ${height - radius} V ${radius} A ${radius} ${radius} 0 0 1 ${radius} 0 Z`;
  let leader: { start: { x: number; y: number }; end: { x: number; y: number } } | undefined;
  if (input.anchor) {
    const dx = input.anchor.x - centre.x, dy = input.anchor.y - centre.y;
    const ax = cos * dx + sin * dy, ay = -sin * dx + cos * dy;
    const inside = (t: number) => {
      const x = Math.abs(ax * t), y = Math.abs(ay * t);
      if (x > width / 2 || y > height / 2) return false;
      return Math.hypot(Math.max(0, x - width / 2 + radius), Math.max(0, y - height / 2 + radius)) <= radius + 1e-9;
    };
    if (!inside(1)) {
      let lo = 0, hi = 1;
      for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        if (inside(mid)) lo = mid; else hi = mid;
      }
      leader = { start: { x: centre.x + dx * lo, y: centre.y + dy * lo }, end: { ...input.anchor } };
    }
  }
  const opacity = Math.max(0, Math.min(1, input.opacity ?? 1));
  return {
    frame, centre, rotation, path, radius, fontSize, padding, lineHeight,
    lines, lineWidths, strokeWidth, leader, anchor: input.anchor,
    anchorRadius: 2.5 * scale, opacity, fillOpacity: 0.95 * opacity,
    overflow: lines.length >= lineLimit || !fitsX || !fitsY || usableWidth <= 0 || lineWidths.some(w => w > usableWidth + 0.000001),
  };
}
